import React from 'react';
import { useTranslation } from 'react-i18next';
import DeviationSelector from '../../DeviationSelector';
import { useCurrentGallectionDeviations } from '../../../_hocs/withCurrentGallectionDeviations';
import { useCurrentGallection } from '../../../_hocs/withCurrentGallection';
import { useCurrentSubfolder } from '../../../_hocs/withCurrentSubfolder';
import LoadingIndicator from '@wix/da-ds/pkg/LoadingIndicator';
import SelectedItemsPanel from '../../../_partials/SelectedItemsPanel';
import PopupInnerLayout from '../../../_partials/PopupInnerLayout';
import SearchInput from '../../../_partials/SearchInput';
import { useSearch } from '../../../_hocs/withSearch';
import NoSearchResults from '../../../_partials/NoSearchResults';
import { useFileUpload } from '../../../_hocs/withFileUpload';
import Flex from '@wix/da-ds/pkg/Flex';
import SelectableSubfolderSlider from '../../../_partials/SelectableSubfolderSlider';
import { GallectionType } from '../../../../types/gallection';

export type Props = {
  /** don't show the search input */
  disableSearch?: boolean;
};

const GallectionDeviationSelector: React.FC<Props> = ({ disableSearch }) => {
  const { t } = useTranslation();
  const { currentSubfolderId } = useCurrentSubfolder();
  const { searchQuery } = useSearch();
  const { fileUploads } = useFileUpload();
  const { currentGallection } = useCurrentGallection();
  const { deviations, isInitialized, isFetching, hasMore, fetchMoreGallectionDeviations } =
    useCurrentGallectionDeviations();

  const fetchMoreGallectionDeviationsIfNeeded = () => {
    if (!hasMore || isFetching) {
      return;
    }
    fetchMoreGallectionDeviations();
  };

  const getEmptyText = () => {
    switch (currentGallection?.type) {
      case GallectionType.STASH:
        return t('mediapopup.empty.stash');
      case GallectionType.GALLERY:
        return t('mediapopup.empty.gallery');
      case GallectionType.COLLECTION:
        return t('mediapopup.empty.collection');
      default:
        return '';
    }
  };

  const renderEmpty = () => {
    if (searchQuery) {
      return <NoSearchResults />;
    }

    return (
      <Flex fullWidth fullHeight direction="column" justifyContent="center" alignItems="center">
        {getEmptyText()}
      </Flex>
    );
  };

  const renderContent = () => {
    return (
      <>
        {isInitialized &&
          (!deviations.length && !fileUploads.length && !isFetching && !hasMore ? (
            renderEmpty()
          ) : (
            <DeviationSelector deviations={deviations} />
          ))}
        {(!isInitialized || isFetching) && (
          <LoadingIndicator view="daSymbol" coverMode={!hasMore && !isFetching} />
        )}
      </>
    );
  };

  return (
    <PopupInnerLayout
      headerExtrasRightZone={disableSearch ? undefined : <SearchInput />}
      headerExtrasBottomZone={
        <>
          {!currentSubfolderId && <SelectableSubfolderSlider />}
          <SelectedItemsPanel />
        </>
      }
      scrollableContent={renderContent()}
      onScrolledToBottom={fetchMoreGallectionDeviationsIfNeeded}
      onNotScrollable={fetchMoreGallectionDeviationsIfNeeded}
    />
  );
};

export default GallectionDeviationSelector;
